import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { didCancel, hash, task, lastValue } from 'ember-concurrency';
import type LocationModel from 'garaje/models/location';
import type NotificationPreferenceModel from 'garaje/models/notification-preference';
import type UserModel from 'garaje/models/user';
import type PluginPreference from 'garaje/types/plugin-preference';
import { MSTEAMS_V2_PLUGIN_KEY, SLACK_V2_PLUGIN_KEY } from 'garaje/utils/enums';
import _sortBy from 'lodash/sortBy';
import { localCopy } from 'tracked-toolbox';

interface NotificationPreferencesLoaderComponentSignature {
  Args: {
    /**
     * If true, a preference for email will always be returned, even if no matching plugin is present.
     */
    alwaysIncludeEmail?: boolean;
    /**
     * If true, a preference for SMS will always be returned, even if no matching plugin is present.
     */
    alwaysIncludeSms?: boolean;
    /**
     * One of `@location` or `@user` MUST be provided. `@location` has precedence if both are passed.
     */
    location?: LocationModel;
    notificationType?: string;
    /**
     * One of `@location` or `@user` MUST be provided. `@location` has precedence if both are passed.
     */
    user?: UserModel;
  };
  Blocks: {
    default: [pluginPreferences: PluginPreference[] | undefined, hasSomeEnabled: boolean, reloadQuery: () => void];
  };
}

export default class NotificationPreferencesLoaderComponent extends Component<NotificationPreferencesLoaderComponentSignature> {
  @service declare store: Store;

  @localCopy('args.notificationType', 'host_notification') declare notificationType: string;
  @lastValue('query') declare pluginPreferences: PluginPreference[] | undefined;
  private _lastPreferences: PluginPreference[] | undefined;

  constructor(owner: unknown, args: NotificationPreferencesLoaderComponentSignature['Args']) {
    super(owner, args);
    void this.query.perform();
  }

  get hasSomeEnabled(): boolean {
    const preferenceValues = this.query.lastSuccessful?.value ?? [];
    return preferenceValues.some((p) => p.preference.preferenceValue);
  }

  get modelType(): string {
    return this.args.location ? 'location' : 'user';
  }

  get model(): LocationModel | UserModel | undefined {
    return this.args.location ?? this.args.user;
  }

  query = task(async () => {
    try {
      const relationId = this.model?.id;
      const { installedPlugins, notificationPreferences } = await hash({
        notificationPreferences: relationId
          ? this.store.query('notification-preference', {
              filter: {
                relation_type: this.modelType,
                relation_id: relationId,
                notification_type: this.notificationType,
              },
            })
          : Promise.resolve([]),
        installedPlugins: this.store.query('plugin', {
          include: 'plugin',
          filter: {
            notification_channels: true,
          },
          page: {
            limit: 100,
          },
        }),
      });

      const notificationPreferencesArray = notificationPreferences.slice();

      const preferences: PluginPreference[] = _sortBy(installedPlugins.slice(), 'name').map((plugin) => {
        return {
          plugin: {
            iconSmall: plugin.iconSmall,
            key: plugin.key,
            name: plugin.name,
            description: plugin.description,
          },
          // There is no real relationship between plugin, model and
          // notification-preferences which force us to do the
          // binding manually.

          ...this.getPreferences(plugin.key, notificationPreferencesArray),
        };
      });

      if (this.args.alwaysIncludeSms && !installedPlugins.find((plugin) => plugin.key === 'sms')) {
        preferences.unshift({
          plugin: {
            iconSmall: htmlSafe('<img src="/assets/images/features/sms.svg" alt="" role="presentation">'),
            key: 'sms',
            name: 'SMS',
            description: 'Send employees an SMS when their visitor arrives.',
          },
          preference: this.findOrCreateNotificationPreference('sms', notificationPreferences.slice()),
        });
      }

      if (this.args.alwaysIncludeEmail && !installedPlugins.find((plugin) => plugin.key === 'email')) {
        preferences.unshift({
          plugin: {
            iconSmall: htmlSafe('<img src="/assets/images/features/email.svg" alt="" role="presentation">'),
            key: 'email',
            name: 'Email',
            description: 'Send employees an email when their visitor arrives.',
          },
          preference: this.findOrCreateNotificationPreference('email', notificationPreferences.slice()),
        });
      }

      this._lastPreferences = preferences;
      return preferences;
    } catch (e) {
      if (didCancel(e)) throw e;
      console.error('[NotificationPreferencesLoader] Failed to load notification preferences:', e); // eslint-disable-line no-console
      return this._lastPreferences;
    }
  });

  getPreferences(
    pluginKey: string,
    notificationPreferences: NotificationPreferenceModel[],
  ): { preference: NotificationPreferenceModel; oldPreference?: NotificationPreferenceModel } {
    const preference = this.findOrCreateNotificationPreference(pluginKey, notificationPreferences);
    let oldPreference = undefined;

    // Some notifications for Slack and Teams are sent by their old channel so find their old channel too and keep it in sync
    if (pluginKey === SLACK_V2_PLUGIN_KEY) {
      oldPreference = this.findOrCreateNotificationPreference(
        'slack',
        notificationPreferences,
        preference.preferenceValue,
      );
    }

    if (pluginKey === MSTEAMS_V2_PLUGIN_KEY) {
      oldPreference = this.findOrCreateNotificationPreference(
        'msteams',
        notificationPreferences,
        preference.preferenceValue,
      );
    }

    return {
      preference,
      oldPreference,
    };
  }

  findOrCreateNotificationPreference(
    pluginKey: string,
    notificationPreferences: NotificationPreferenceModel[],
    preferenceValue?: boolean,
  ): NotificationPreferenceModel {
    const preference = notificationPreferences.find((pref) => pref.notificationPlatformKey === pluginKey);

    if (preference) return preference;

    // Hub treats notifications as enabled by default for new users when no preference
    // record exists, so the toggle should reflect that. Location-level configs keep
    // the previous off-by-default behavior.
    const defaultValue = preferenceValue ?? this.modelType === 'user';

    return this.store.createRecord('notification-preference', {
      notificationPlatformKey: pluginKey,
      notificationType: this.notificationType,
      preferenceValue: defaultValue,
      relationId: this.model?.id,
      relationType: this.modelType,
    });
  }

  <template>{{yield this.pluginPreferences this.hasSomeEnabled this.query.perform}}</template>
}
